import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, CalendarDays, MapPin } from "lucide-react";
import { API, authHeaders, fileUrl, formatWhen, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EventForm } from "./EventForm";
import { ConfirmDelete } from "./ConfirmDelete";

const EventRow = ({ ev, onDelete }) => {
  const [l1, l2] = formatWhen(ev.start, ev.end);
  return (
    <li className={`flex gap-4 sm:gap-5 bg-white border border-[#E4DCF2] rounded-2xl p-4 ${ev.is_past ? "opacity-70" : ""}`} data-testid={`admin-event-row-${ev.id}`}>
      <div className="w-20 sm:w-24 shrink-0 aspect-[2/3] rounded-lg overflow-hidden bg-[#F6F1FD]">
        {ev.flyer_url && <img src={fileUrl(ev.flyer_url)} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Badge className={ev.is_past ? "bg-[#5E5672] hover:bg-[#5E5672]" : "bg-[#1E9E5A] hover:bg-[#1E9E5A]"} data-testid="admin-event-status-badge">
            {ev.is_past ? "Past" : "Upcoming"}
          </Badge>
          {ev.tag && <span className="text-xs font-bold text-[#D99A00]">{ev.tag}</span>}
        </div>
        <h3 className="font-display text-2xl leading-tight truncate" data-testid="admin-event-title">{ev.title}</h3>
        <p className="text-sm text-[#5E5672] flex items-start gap-1.5 mt-1"><CalendarDays className="w-4 h-4 mt-0.5 shrink-0" />{l1} · {l2}</p>
        {ev.location && <p className="text-sm text-[#5E5672] flex items-start gap-1.5"><MapPin className="w-4 h-4 mt-0.5 shrink-0" />{ev.location.replace(/\n/g, ", ")}</p>}
      </div>
      <div className="shrink-0">
        <ConfirmDelete
          title={`Delete "${ev.title}"?`}
          description="This permanently removes the event and its flyer from the website."
          onConfirm={() => onDelete(ev.id)}
          testId={`admin-delete-event-${ev.id}`}
        />
      </div>
    </li>
  );
};

export const EventsTab = ({ onUnauthorized }) => {
  const [events, setEvents] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const handleErr = useCallback((err) => {
    if (err.response?.status === 401) return onUnauthorized();
    toast.error(formatApiErrorDetail(err.response?.data?.detail));
  }, [onUnauthorized]);

  const load = useCallback(() => {
    axios.get(`${API}/admin/events`, { headers: authHeaders() }).then((r) => setEvents(r.data)).catch(handleErr);
  }, [handleErr]);

  useEffect(load, [load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/events/${id}`, { headers: authHeaders() });
      toast.success("Event deleted");
      load();
    } catch (err) { handleErr(err); }
  };

  return (
    <section data-testid="admin-events-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-4xl">Events</h2>
          <p className="text-[#5E5672] text-sm">All events, including past ones. Only upcoming events show on the homepage.</p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)} className="rounded-full bg-[#5A2D9C] hover:bg-[#7B3FE4] font-extrabold h-11 px-5" data-testid="admin-add-event-toggle">
          {showForm ? <><X className="w-4 h-4" /> Close form</> : <><Plus className="w-4 h-4" /> Add event</>}
        </Button>
      </div>
      {showForm && <EventForm onCreated={() => { setShowForm(false); load(); }} onError={handleErr} />}
      {events === null && <p className="text-[#5E5672]">Loading…</p>}
      {events?.length === 0 && <p className="text-[#5E5672]" data-testid="admin-no-events">No events yet. Add your first one.</p>}
      <ul className="grid gap-3" data-testid="admin-events-list">
        {events?.map((ev) => <EventRow key={ev.id} ev={ev} onDelete={remove} />)}
      </ul>
    </section>
  );
};
